<script setup lang="ts">
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { computed, defineAsyncComponent, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { routeBase } from '@/config/router'
import BaseLayout from './components/BaseLayout.vue'
import NavigationButtonPanel from './components/sidebar/NavigationButtonPanel.vue'
import type { TabItem } from './components/sidebar/NavigationButtonPanel.vue'
import MobileBottomNav from './components/sidebar/MobileBottomNav.vue'
import LoggedOutSidebar from './components/sidebar/LoggedOutSidebar.vue'
import LoggedOutMobileBar from './components/sidebar/LoggedOutMobileBar.vue'
import DesktopMain from './components/DesktopMain.vue'
import MobileMain from './components/MobileMain.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { useKeycloakHealth } from './composables/useKeycloakHealth'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import { fetchCurrentUser } from '@/api/bff_api'
import { useUserStore } from '@/stores/user_store'
import { useAddClientFormStore } from '@/stores/add_client_form_store'
import { useAddPaymentFormStore } from '@/stores/add_payment_form_store'
import { useAddServiceCategoryFormStore } from '@/stores/add_service_category_form_store'
import { useAddServiceTypeFormStore } from '@/stores/add_service_type_form_store'
import { useAddSubscriptionTypeFormStore } from '@/stores/add_subscription_type_form_store'
import { useAddBusinessServiceFormStore } from '@/stores/add_business_service_form_store'
import { useAddBookingTypeFormStore } from '@/stores/add_booking_type_form_store'
import { useAddGroupFormStore } from '@/stores/add_group_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'

/*
 * Lazy-loaded: these components transitively import @/api/* → @/api/http →
 * @/router. Static import here (App.vue evaluates before the router is
 * finalized) would create the same boot-time circular-evaluation issue as
 * InfoViewLayout's AddClientForm. Deferring to component-mount time is safe
 * because the router is fully initialized by then.
 */
const AddClientFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/clientForm/AddClientFormMobileScreen.vue'),
)
const AddPaymentFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/paymentForm/AddPaymentFormMobileScreen.vue'),
)
const AddServiceCategoryFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/serviceCategoryForm/AddServiceCategoryFormMobileScreen.vue'),
)
const AddServiceTypeFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/serviceTypeForm/AddServiceTypeFormMobileScreen.vue'),
)
const AddSubscriptionTypeFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/subscriptionTypeForm/AddSubscriptionTypeFormMobileScreen.vue'),
)
const AddServiceFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/serviceForm/AddServiceFormMobileScreen.vue'),
)
const AddBookingTypeFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/bookingTypeForm/AddBookingTypeFormMobileScreen.vue'),
)
const AddGroupFormMobileScreen = defineAsyncComponent(
  () => import('./components/main/groupForm/AddGroupFormMobileScreen.vue'),
)

// Start Keycloak health query (shared cache for redirectToLogin gate)
useKeycloakHealth()

const { isMobile } = useIsMobile()
const { hasRole } = usePermissions()

/*
 * Bootstraps `userStore.userInfo` at app-shell mount time so `hasRole()`
 * checks below (and everywhere else in the authenticated app) have role
 * data on first render, not just after the user happens to visit Welcome
 * (the only other place this call is made). Mirrors WelcomeTab's own
 * `fetchCurrentUser()` call/tolerance exactly: `null` means "not signed in
 * yet", a normal state here, not an error to surface — `hasRole()` simply
 * denies by default until this resolves.
 */
const userStore = useUserStore()
onMounted(async () => {
  if (userStore.isLoggedInProfileReady) return
  try {
    const me = await fetchCurrentUser()
    if (me) userStore.setUserInfo(me)
  } catch (error) {
    // Unexpected failure (network/5xx) — leave userInfo null, same as an
    // unauthenticated session; no user-facing error surface at this level.
    console.error('[App] fetchCurrentUser failed', error)
  }
})

// Standalone routes (e.g. the public /statement page) render with no app
// shell at all — no sidebar, no nav, no auth-gated layout — just their own
// component, since they're meant to work for anonymous, logged-out visitors.
const route = useRoute()
const isStandalone = computed(() => route.meta.standalone === true)

// Gates the tab panel, logout button, and Home tab: none of them make sense before a
// session exists. Only the language switcher (LoggedOutSidebar / LoggedOutMobileBar)
// shows while logged out.
const isLoggedIn = computed(() => userStore.isLoggedInProfileReady)

const addClientForm = useAddClientFormStore()
const addPaymentForm = useAddPaymentFormStore()
const addServiceCategoryForm = useAddServiceCategoryFormStore()
const addServiceTypeForm = useAddServiceTypeFormStore()
const addSubscriptionTypeForm = useAddSubscriptionTypeFormStore()
const addBusinessServiceForm = useAddBusinessServiceFormStore()
const addBookingTypeForm = useAddBookingTypeFormStore()
const addGroupForm = useAddGroupFormStore()

const { t } = useI18n()

/*
 * Computed (not a plain const) so labels re-translate when the user
 * switches language via LocaleSwitcher, and so tabs appear/disappear
 * immediately once `userStore.userInfo` resolves (see the bootstrap
 * above) without a page reload. Each tab is gated on its resource's view
 * role; each `onAdd` is independently gated on the corresponding create
 * role — a viewer without create rights still sees the tab, just no "+".
 * Rules/Diseases stay omitted entirely per the existing v0.6.0 TODO below,
 * unrelated to permissions.
 */
const tabs = computed<TabItem[]>(() => {
  const result: TabItem[] = []

  if (hasRole(ResourceAccessRole.BusinessServicesView)) {
    result.push({
      path: `${routeBase}/services`,
      label: t('nav.services'),
      onAdd: hasRole(ResourceAccessRole.BusinessServicesCreate)
        ? () => {
            clearInfoViewSelections('addBusinessServiceForm')
            addBusinessServiceForm.open()
          }
        : undefined,
    })
  }

  if (hasRole(ResourceAccessRole.PaymentsView)) {
    result.push({
      path: `${routeBase}/payments`,
      label: t('nav.payments'),
      onAdd: hasRole(ResourceAccessRole.PaymentsCreate)
        ? () => {
            clearInfoViewSelections('addPaymentForm')
            addPaymentForm.open()
          }
        : undefined,
    })
  }

  if (hasRole(ResourceAccessRole.ClientsView)) {
    result.push({
      path: `${routeBase}/clients`,
      label: t('nav.clients'),
      onAdd: hasRole(ResourceAccessRole.ClientsCreate)
        ? () => {
            clearInfoViewSelections('addClientForm')
            addClientForm.open()
          }
        : undefined,
    })
  }

  // TODO: Add rules and diseases in v0.6.0
  // { path: `${routeBase}/rules`, label: t('nav.rules') },
  // { path: `${routeBase}/diseases`, label: t('nav.diseases') },

  if (hasRole(ResourceAccessRole.BusinessServicesClientVisitCountsView)) {
    result.push({ path: `${routeBase}/finances`, label: t('nav.finances') })
  }

  if (hasRole(ResourceAccessRole.PerformersStatView)) {
    result.push({ path: `${routeBase}/stuff`, label: t('nav.stuff') })
  }

  if (hasRole(ResourceAccessRole.BookingTypesView) || hasRole(ResourceAccessRole.BookingsView)) {
    result.push({
      path: `${routeBase}/booking`,
      label: t('nav.booking'),
      onAdd: hasRole(ResourceAccessRole.BookingTypesCreate)
        ? () => {
            clearInfoViewSelections('addBookingTypeForm')
            addBookingTypeForm.open()
          }
        : undefined,
    })
  }

  // Welcome is visible to any signed-in user — it's the profile/greeting tab, not gated
  // behind any resource permission. Omitted entirely while logged out: the tab panel
  // itself doesn't render pre-login (see isLoggedIn / LoggedOutSidebar), but the Home
  // tab is left out of the list too so it can't reappear from any other consumer of
  // `tabs`.
  if (isLoggedIn.value) {
    result.push({ path: `${routeBase}/welcome`, label: t('nav.home') })
  }

  return result
})
</script>

<template>
  <RouterView v-if="isStandalone" />

  <template v-else>
    <BaseLayout :collapsible="isLoggedIn">
      <template #header> </template>

      <template #sidebar>
        <NavigationButtonPanel v-if="!isMobile && isLoggedIn" :tabs="tabs" />
        <LoggedOutSidebar v-else-if="!isMobile" />
      </template>

      <template #main>
        <DesktopMain v-if="!isMobile" />
        <MobileMain v-else />
      </template>
    </BaseLayout>
    <MobileBottomNav v-if="isMobile && isLoggedIn" :tabs="tabs" />
    <LoggedOutMobileBar v-else-if="isMobile" />
    <AddClientFormMobileScreen v-if="isMobile && addClientForm.isOpen" />
    <AddPaymentFormMobileScreen v-if="isMobile && addPaymentForm.isOpen" />
    <AddServiceCategoryFormMobileScreen v-if="isMobile && addServiceCategoryForm.isOpen" />
    <AddServiceTypeFormMobileScreen v-if="isMobile && addServiceTypeForm.isOpen" />
    <AddSubscriptionTypeFormMobileScreen v-if="isMobile && addSubscriptionTypeForm.isOpen" />
    <AddServiceFormMobileScreen
      v-if="isMobile && addBusinessServiceForm.isOpen"
      :key="addBusinessServiceForm.editId ?? 'new'"
    />
    <AddBookingTypeFormMobileScreen v-if="isMobile && addBookingTypeForm.isOpen" />
    <AddGroupFormMobileScreen v-if="isMobile && addGroupForm.isOpen" />
    <VueQueryDevtools />
  </template>
</template>

<style scoped></style>
